/**
 * The ask box.
 *
 * This is the front door of the whole product, so it is a plain-language
 * invitation rather than a form: one auto-growing field, no required category,
 * no ticket type to pick. The user writes what is wrong in whatever language
 * they think in, and the `content_lang` sent with it records which language
 * that was (§3.6 — writes are stored verbatim, never translated).
 *
 * Enter sends, Shift+Enter starts a new line. That is what everyone expects
 * from a chat box, and the hint says so rather than leaving it to be guessed.
 * On a touch screen Enter is the keyboard's return key and people expect a new
 * line, so there it only inserts one and the send button sends (no hint).
 */

import { Icon, Send, Square } from "@norican/icons";
import { Button, cn, Field, Form, Textarea, Typography } from "@norican/react";
import {
  forwardRef,
  type KeyboardEvent,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";

import { AttachmentUploader, usePasteToAttach } from "@/components/attachments";
import { useT } from "@/i18n";

import type { ChatFile } from "./lib/chat-files";
import { useTypedExamples } from "./use-typed-examples";

/** Contract limit for `AssistantMessageCreateRequest.content`. */
export /**
 * The phrases the composer cycles, in order.
 *
 * Listed explicitly rather than read off the translation object with
 * `returnObjects`, so a key missing from one language is a visible fallback
 * rather than a silently shorter rotation in that locale only.
 */
const EXAMPLE_KEYS = [
  "outlook",
  "mailbox",
  "printer",
  "powerBi",
  "purchase",
  "software",
  "vpn",
  "tickets",
  "d365fo",
  "monitor",
] as const;

export const MESSAGE_MAX_LENGTH = 8000;

/** A touch-first device, where Enter means "new line" and the button sends. */
function isCoarsePointer(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(pointer: coarse)").matches
  );
}

/** Show the remaining-characters hint only once the limit is in sight. */
const COUNTER_THRESHOLD = 0.8;

export interface ComposerProps {
  /**
   * Called with the trimmed question and the `attachment_id`s of the files the
   * user attached to this turn. The parent owns sending both; the server
   * injects the ids into whatever the turn files (§ safety property), so the
   * model never chooses them.
   *
   * The draft (text and attachments) is cleared only once the send is
   * accepted: return — or resolve — `false` when it failed and the composer
   * keeps everything, so a dropped connection, an expired session or a busy
   * conversation never throws away what the user wrote.
   */
  onSubmit: (
    content: string,
    attachments: string[],
    /** The same files with their names, so a form card can show what it carries. */
    files?: ChatFile[],
  ) => boolean | Promise<boolean> | void;
  /** True while a reply is arriving — the field locks and Stop appears. */
  isStreaming?: boolean;
  /** Cancels the in-flight reply. Stop is hidden when this is omitted. */
  onStop?: () => void;
  /** Blocks sending (e.g. the conversation is still being created). */
  disabled?: boolean;
  /** Pre-fills the box, e.g. from a suggested prompt. */
  value?: string;
  onValueChange?: (value: string) => void;
  autoFocus?: boolean;
  /**
   * True once a conversation is under way. The composer is the only input on
   * the page, so mid-conversation it has to say so: with the same "What do you
   * need help with?" label throughout, answering the assistant's question
   * looks indistinguishable from asking a brand-new one, and users reasonably
   * conclude there is nowhere to reply.
   */
  isReply?: boolean;
  className?: string;
}

/**
 * Imperative handle so the page can put the caret back in the box — used by
 * "New question", which otherwise has no visible effect when the conversation
 * is already empty.
 */
export interface ComposerHandle {
  /**
   * `preventScroll` matters: focus is returned here the moment an answer
   * finishes, and a reader who has scrolled up to re-read something must not be
   * dragged back down by it.
   */
  focus: (options?: FocusOptions) => void;
  /**
   * Sends what is in the box now, exactly as the send button would — used by
   * the page's "Try again" after a send that was not accepted, so the retry
   * carries the kept draft and its attachments.
   */
  submit: () => void;
}

export const Composer = forwardRef<ComposerHandle, ComposerProps>(function Composer(
  {
    onSubmit,
    isStreaming = false,
    onStop,
    disabled = false,
    value,
    onValueChange,
    autoFocus = false,
    isReply = false,
    className,
  },
  ref,
) {
  const { t } = useT("assistant");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [internal, setInternal] = useState("");
  const [touchedEmpty, setTouchedEmpty] = useState(false);

  // The files attached to the turn being composed. The uploader owns the
  // upload interaction and reports back the committed ids; bumping its `key`
  // on send is what clears the chips without leaving a stale id behind.
  const [attachments, setAttachments] = useState<string[]>([]);
  const [attachmentFiles, setAttachmentFiles] = useState<ChatFile[]>([]);
  const [attachmentsPending, setAttachmentsPending] = useState(false);
  const [uploaderKey, setUploaderKey] = useState(0);
  // Read once: a device does not switch between touch and mouse mid-draft
  // often enough to justify a media-query listener.
  const [touchInput] = useState(isCoarsePointer);

  const controlled = value !== undefined;
  const content = controlled ? value : internal;
  const exampleHintId = useId();

  /**
   * Paste a screenshot straight into the message box.
   *
   * Ctrl+V after a Print Screen is how people share what they are looking at,
   * and requiring them to save a file first and then find it in a picker is the
   * step at which most of them give up and describe the error in prose instead.
   *
   * The handler sits on the form rather than on the textarea. It behaves the
   * same for anyone typing — the textarea is inside the form and `paste`
   * bubbles — and it is the same hook every other attachment surface uses, so
   * the naming of a pasted bitmap lives in one place instead of two. Text
   * pastes and the interception rules are documented on the hook.
   */
  const paste = usePasteToAttach({ disabled: disabled || isStreaming });

  /* The cycling examples are decoration for sighted users only. The textarea
     keeps one real, translated `placeholder` (the first example) as its
     accessible hint, and the animated copy is painted over the top in an
     `aria-hidden` layer — so assistive technology hears one stable sentence
     instead of a new one every 55ms.

     It runs only on the first, empty composer: never on a reply, never once
     there is a draft, and never while the field has focus. */
  const examples = useMemo(() => EXAMPLE_KEYS.map((key) => t(`composer.examples.${key}`)), [t]);
  /* Deliberately NOT conditioned on focus. The landing composer autofocuses so
     someone can arrive and type, which made "stop when focused" mean "never
     run" — the field is focused before the first frame. What must not happen is
     the animation painting under text someone is writing, and `content === ""`
     is the condition that actually guarantees that. Typing a character stops it
     mid-word; clearing the box brings it back. */
  const showExamples = !isReply && content === "" && !disabled && !isStreaming;
  const typed = useTypedExamples({ examples, active: showExamples });

  const setContent = (next: string) => {
    if (touchedEmpty && next.trim() !== "") {
      setTouchedEmpty(false);
    }
    if (controlled) {
      onValueChange?.(next);
    } else {
      setInternal(next);
    }
  };

  const submit = () => {
    const trimmed = content.trim();
    if (trimmed === "") {
      setTouchedEmpty(true);
      textareaRef.current?.focus();
      return;
    }
    const clearDraft = () => {
      setContent("");
      // Clear the attachments with the text: remounting the uploader drops its
      // chips and its own state so the next turn starts empty.
      setAttachments([]);
      setAttachmentFiles([]);
      setAttachmentsPending(false);
      setUploaderKey((key) => key + 1);
    };
    // Kept until the parent says the send was accepted. The field and the
    // uploader are locked while it is in flight, so nothing typed meanwhile can
    // be lost by clearing afterwards.
    const result = onSubmit(
      trimmed,
      attachments,
      attachmentFiles.filter((file) => attachments.includes(file.attachmentId)),
    );
    if (result instanceof Promise) {
      void result.then((accepted) => {
        if (accepted) {
          clearDraft();
        }
      });
    } else if (result !== false) {
      clearDraft();
    }
  };

  /* Re-created every render on purpose: `submit` closes over the current
     draft, and the page's "Try again" must send what is in the box now. */
  useImperativeHandle(ref, () => ({
    focus: (options?: FocusOptions) => textareaRef.current?.focus(options),
    submit: () => {
      if (!isStreaming && !disabled && !attachmentsPending) {
        submit();
      }
    },
  }));

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }
    // IME composition (Chinese, Japanese, Korean) uses Enter to accept a
    // candidate. Sending on that would truncate the word being typed. Some
    // browsers (Safari) report the accepting keydown only as keyCode 229.
    if (event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }
    // Touch keyboards: let Enter insert the newline; the send button sends.
    if (touchInput) {
      return;
    }
    event.preventDefault();
    if (!isStreaming && !disabled && !attachmentsPending) {
      submit();
    }
  };

  const remaining = MESSAGE_MAX_LENGTH - content.length;
  const showCounter = content.length > MESSAGE_MAX_LENGTH * COUNTER_THRESHOLD;

  return (
    <Form
      className={cn("flex flex-col gap-3", className)}
      onFormSubmit={() => submit()}
      onPaste={paste.onPaste}
    >
      {/* No instruction block above the box any more: the hero directly above
          says "Tell us what's going on, or what you need.", and the rotating
          examples inside the field show the register to write in. One
          prominent input, nothing competing with it. */}

      {/* No visible label on the first composer. The page heading already says
          "Ask IT anything." directly above it, and repeating "What do you need
          help with?" underneath made the box read as a form field rather than
          the start of a conversation. The label still exists for assistive
          technology through `aria-label`. A reply keeps its visible label,
          because by then the heading is far up the transcript. */}
      <Field
        name="content"
        label={undefined}
        hint={showCounter ? t("composer.remaining", { count: remaining }) : undefined}
        error={touchedEmpty ? t("composer.empty") : undefined}
      >
        <div className="relative">
          <Textarea
            ref={textareaRef}
            rows={3}
            autoResize
            autoFocus={autoFocus}
            aria-label={t("composer.label")}
            aria-describedby={showExamples ? exampleHintId : undefined}
            maxLength={MESSAGE_MAX_LENGTH}
            /* Suppressed while the overlay paints, or the browser's own
               placeholder renders underneath the animation and the two read as
               one smeared line. The static wording is still available to
               assistive technology through `aria-describedby` below. */
            /* The overlay owns the field for the whole cycle, not only while
               it has characters in it. Keying this on `typed !== ""` meant that
               the instant a phrase finished deleting, the native placeholder
               flashed a different sentence for a frame before the next phrase
               started — which is most of what read as jitter. Between phrases
               the field now shows a waiting cursor and nothing else. */
            placeholder={
              showExamples
                ? undefined
                : isReply
                  ? t("composer.replyPlaceholder")
                  : t("composer.askPlaceholder")
            }
            className="max-h-64"
            value={content}
            disabled={disabled || isStreaming}
            onChange={(event) => setContent(event.target.value)}
            onKeyDown={handleKeyDown}
          />

          {showExamples ? (
            <>
              {/* Announced once, never re-announced: the rotation is decoration
                  and the screen reader gets one stable sentence. */}
              <span id={exampleHintId} className="sr-only">
                {t("composer.placeholder")}
              </span>

              {/* Geometry is copied from the design system's own textarea rule
                  rather than guessed: padding-block `space-2`, padding-inline
                  `space-3`, `font-size-sm`, normal leading — which Tailwind's
                  py-2 / px-3 / text-sm map onto through the DS token bridge.
                  `top-px left-px` accounts for the control's 1px border, so the
                  first character lands exactly where a typed one would.
                  `max-sm:text-base` follows the phone-width 16px field rule in
                  globals.css (it stops iOS zooming into the box).

                  `pointer-events-none` keeps clicks going to the field, and
                  `aria-hidden` keeps the animation out of the accessibility
                  tree entirely. */}
              <span
                aria-hidden="true"
                className="text-fg-subtle pointer-events-none absolute top-px left-px max-w-[calc(100%-2px)] px-3 py-2 text-sm leading-normal font-normal select-none max-sm:text-base"
              >
                {typed}
                <span className="bg-fg-subtle ml-px inline-block h-[1em] w-px translate-y-[0.15em] animate-pulse" />
              </span>
            </>
          ) : null}
        </div>
      </Field>

      {/* One control row: attach on the left, the keyboard hint beside it in
          muted caption, send anchored right. `items-center` on the row rather
          than spacing each child is what stops the hint floating at its own
          baseline between two taller controls, and `flex-wrap` lets the hint
          drop to a second line on a narrow screen without breaking the pair. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <AttachmentUploader
          ref={paste.ref}
          key={uploaderKey}
          disabled={disabled || isStreaming}
          triggerLabel={t("composer.attach")}
          onChange={setAttachments}
          onFilesChange={setAttachmentFiles}
          onPendingChange={setAttachmentsPending}
        />

        <Typography variant="caption" tone="muted" className="min-w-0">
          {attachmentsPending
            ? t("composer.attachmentsUploading")
            : isStreaming
              ? t("a11y.streaming")
              : touchInput
                ? null
                : t("composer.keyboardHint")}
        </Typography>

        <div className="ml-auto flex items-center gap-2">
          {isStreaming && onStop !== undefined ? (
            <Button
              type="button"
              variant="outline"
              tone="neutral"
              startIcon={<Icon icon={Square} size="sm" />}
              onClick={onStop}
            >
              {t("composer.stop")}
            </Button>
          ) : null}

          <Button
            type="submit"
            disabled={disabled || isStreaming || attachmentsPending}
            loading={isStreaming}
            startIcon={<Icon icon={Send} size="sm" />}
          >
            {isStreaming ? t("composer.sending") : t("composer.send")}
          </Button>
        </div>
      </div>
    </Form>
  );
});
